import { Accordion } from "@JohnLewisPartnership/jl-design-system/dist/components/accordion/Accordion";
import { AccordionItem } from "@JohnLewisPartnership/jl-design-system/dist/components/accordion/accordion-item/AccordionItem";
import classNames from "classnames";
import { Facet, FacetDetail } from "@/types/productListing";
import { slugify } from "@/utils/slugify";
import { FacetItem } from "../FacetItem/FacetItem";
import styles from "./FacetGroup.module.scss";
import { useCallback, useState } from "react";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { FacetSearch } from "../FacetSearch/FacetSearch";
import { sendMonetateEvent } from "@/utils/sendMonetateEvent";

const SEARCH_LENGTH = 20;
const INITIAL_NUMBER_TO_SHOW = 8;
const MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING = 10;

type FacetGroupProps = {
  dispatchFacetAnalytics: (
    eventType: string,
    facetName: string,
    facetDetails: FacetDetail,
  ) => void;
  facet: Facet;
  open?: boolean;
  position?: number;
};

export const FacetGroup = ({
  dispatchFacetAnalytics,
  facet,
  open = true,
  position = 1,
}: FacetGroupProps) => {
  const [isFacetListExpanded, setIsFacetListExpanded] = useState(false);
  const [facetSearchValue, setFacetSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(open);

  const filteredFacetDetailsList = facet.details.filter((facetDetails) =>
    facetSearchValue
      ? facetDetails.label
          .toLocaleLowerCase()
          .includes(facetSearchValue.toLowerCase())
      : true,
  );

  const isShowMoreOrFewerVisible =
    filteredFacetDetailsList.length > MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING;

  const numberOfOwnBrandItems = filteredFacetDetailsList
    .filter((facetDetails) =>
      facetSearchValue
        ? facetDetails.label
            .toLocaleLowerCase()
            .includes(facetSearchValue.toLowerCase())
        : true,
    )
    .filter(({ isBoosted }) => isBoosted === "true").length;

  const numberOfValuesToShow =
    isShowMoreOrFewerVisible && !isFacetListExpanded
      ? INITIAL_NUMBER_TO_SHOW
      : filteredFacetDetailsList.length;

  const showMoreOrFewerButton = useCallback(
    () => (
      <UnderlinedButton
        className={styles.facetListShowMore}
        onClick={() => {
          setIsFacetListExpanded(!isFacetListExpanded);
        }}
        data-testid={`facet-show-${isFacetListExpanded ? "fewer" : "more"}-button`}
      >
        Show{" "}
        {isFacetListExpanded
          ? "fewer"
          : `${filteredFacetDetailsList.length - numberOfValuesToShow} more`}
      </UnderlinedButton>
    ),
    [
      isFacetListExpanded,
      filteredFacetDetailsList.length,
      numberOfValuesToShow,
    ],
  );

  const isFacetListSearchable = facet.details.length > SEARCH_LENGTH;

  const selectedOptions = facet.details.filter(
    (detail) => detail.isSelected === "true",
  );

  const showSelectedFacetsSummary = selectedOptions.length > 0;

  return (
    <Accordion size="large">
      <AccordionItem
        className={styles.facetGroup}
        data-testid={`accordion-${facet.name}`}
        data-test2={`position${position}`}
        data-label={facet.name}
        onStateChange={(isOpen: boolean) => {
          setIsOpen(isOpen);
        }}
        header={
          <div className={styles.collapsedSummaryWrapper}>
            <h3 className={styles.facetLabel} id={slugify(facet.name)}>
              {facet.name}
            </h3>
            {showSelectedFacetsSummary && (
              <p
                className={classNames(styles.selectedSummary, {
                  [styles.selectedSummary__hidden!]: isOpen,
                })}
              >
                {selectedOptions
                  .map(({ label, qty }) => `${label} (${qty})`)
                  .join(", ")}
              </p>
            )}
          </div>
        }
        isOpen={open}
      >
        {isFacetListSearchable && (
          <FacetSearch
            label={facet.name}
            facetSearchValue={facetSearchValue}
            setFacetSearchValue={setFacetSearchValue}
          />
        )}
        <ul className={styles.facetList}>
          {filteredFacetDetailsList.map((facetDetails, ind) => (
            <li
              key={facetDetails.facetUrl}
              className={classNames(styles.facetListItem, {
                [styles.facetListItem__divider!]:
                  ind === numberOfOwnBrandItems - 1,
              })}
              hidden={ind >= numberOfValuesToShow}
              data-testid="facet-list-item"
              onClick={() => {
                if (position <= 5)
                  sendMonetateEvent("plp.facets.interaction.top5");
              }}
            >
              <FacetItem
                dimensionName={facet.dimensionName}
                dispatchFacetAnalytics={dispatchFacetAnalytics}
                facetDetails={facetDetails}
                facetName={facet.name}
                setFacetSearchValue={setFacetSearchValue}
              />
              {ind === INITIAL_NUMBER_TO_SHOW - 1 &&
                isFacetListSearchable &&
                isShowMoreOrFewerVisible &&
                isFacetListExpanded &&
                showMoreOrFewerButton()}
            </li>
          ))}
          {filteredFacetDetailsList.length === 0 && (
            <li data-testid="facet-search-no-results">
              No results were found for &apos;{facetSearchValue}&apos;. Please
              try another search.
            </li>
          )}
        </ul>
        {isShowMoreOrFewerVisible && showMoreOrFewerButton()}
      </AccordionItem>
    </Accordion>
  );
};
